import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Checkbox,
  Chip,
  Tooltip
} from '@mui/material';
import { AccountBalance as TaxIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
  SupplierAliasMappingsDialog,
  formatSupplierAliasLabel,
  truncateMiddle,
  type SupplierAliasApi
} from '../components/SupplierAliasMappingsDialog';
import { SupplierPortalCredentialsDialog } from '../components/SupplierPortalCredentialsDialog';
import { isActiveCategoryApiRow } from '../utils/categoryActive';

import { FIT_TABLE_SX, FIT_TABLE_CONTAINER_SX, FitColgroup, fitTruncateSx, fitChipSx } from '../utils/fitTable';
type Category = {
  id: string;
  name: string;
  description: string;
  vatRate?: string;
  plClass: string;
};

type Supplier = {
  id: string;
  name: string;
  invoiceFrequency: string;
  invoiceCurrency: string;
  vatStatus: string;
  notes: string;
  hasInvoicePortalPassword?: boolean;
  invoicePortalUsername?: string;
};

const PL_CLASS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'unset', label: 'Unset' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'internal_transfer', label: 'Internal transfer' },
  { value: 'financing', label: 'Financing' },
  { value: 'capital', label: 'Capital (property purchases)' },
  { value: 'tax', label: 'Tax' },
  { value: 'reversal', label: 'Reversal' },
];

const plClassLabel = (value: string | undefined): string =>
  PL_CLASS_OPTIONS.find((o) => o.value === value)?.label || 'Unset';

const VAT_RATES = ['0%', '5%', '9%', '13.5%', '20%', '21%', '23%'];
const INVOICE_FREQUENCIES = ['Weekly', 'Monthly', 'Quarterly', 'Annually', 'Ad hoc'];
const VAT_STATUSES = ['VAT Registered', 'VAT Exempt', 'VAT Reverse Charge', 'Unknown'];
const CURRENCIES = ['EUR', 'GBP', 'USD', 'Other'];
const TAB_KEYS = ['vat-returns', 'annual-returns', 'categories', 'suppliers'];

export const TaxReturns: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManageSupplierAliases = user?.role === 'owner' || user?.role === 'admin';

  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryVatRate, setCategoryVatRate] = useState(VAT_RATES[4]);
  const [categoryPlClass, setCategoryPlClass] = useState('unset');
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryError, setCategoryError] = useState('');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCategoryBulkOpen, setIsCategoryBulkOpen] = useState(false);
  const [categoryBulkText, setCategoryBulkText] = useState('');
  const [categoryBulkError, setCategoryBulkError] = useState('');
  const [categoryBulkSuccess, setCategoryBulkSuccess] = useState('');
  const [categoryBulkVatRate, setCategoryBulkVatRate] = useState('');
  const [categoryBulkDescription, setCategoryBulkDescription] = useState('');
  const [categoryBulkPlClass, setCategoryBulkPlClass] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const [supplierName, setSupplierName] = useState('');
  const [supplierFrequency, setSupplierFrequency] = useState(INVOICE_FREQUENCIES[1]);
  const [supplierCurrency, setSupplierCurrency] = useState(CURRENCIES[0]);
  const [supplierVatStatus, setSupplierVatStatus] = useState(VAT_STATUSES[3]);
  const [supplierNotes, setSupplierNotes] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierError, setSupplierError] = useState('');
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isSupplierBulkOpen, setIsSupplierBulkOpen] = useState(false);
  const [supplierBulkText, setSupplierBulkText] = useState('');
  const [supplierBulkError, setSupplierBulkError] = useState('');
  const [supplierBulkSuccess, setSupplierBulkSuccess] = useState('');
  const [supplierBulkFrequency, setSupplierBulkFrequency] = useState('');
  const [supplierBulkCurrency, setSupplierBulkCurrency] = useState('');
  const [supplierBulkVatStatus, setSupplierBulkVatStatus] = useState('');
  const [supplierBulkNotes, setSupplierBulkNotes] = useState('');
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);

  const [aliasesBySupplier, setAliasesBySupplier] = useState<Record<string, SupplierAliasApi[]>>({});
  const [supplierAliasMappingsSupplier, setSupplierAliasMappingsSupplier] = useState<Supplier | null>(null);
  const [portalCredentialsSupplier, setPortalCredentialsSupplier] = useState<Supplier | null>(null);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
    fetchSupplierAliases();
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const nextIndex = tabParam ? TAB_KEYS.indexOf(tabParam) : 0;
    if (nextIndex >= 0 && nextIndex !== activeTab) {
      setActiveTab(nextIndex);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    const currentKey = TAB_KEYS[activeTab] || TAB_KEYS[0];
    if (searchParams.get('tab') !== currentKey) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', currentKey);
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    setSelectedCategoryIds((prev) => prev.filter((id) => categories.some((category) => category.id === id)));
  }, [categories]);

  useEffect(() => {
    setSelectedSupplierIds((prev) => prev.filter((id) => suppliers.some((supplier) => supplier.id === id)));
  }, [suppliers]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories', {
        params: { is_active: 'true' }
      });
      setCategories(
        (response.data.categories || [])
          .filter(isActiveCategoryApiRow)
          .map((category: any) => ({
            id: String(category.id),
            name: category.name,
            description: category.description || '',
            vatRate: category.vat_rate || '',
            plClass: String(category.pl_class || 'unset'),
          }))
      );
    } catch (err) {
      setCategoryError('Failed to load categories.');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/api/suppliers', {
        params: { is_active: true }
      });
      setSuppliers(
        (response.data.suppliers || []).map((supplier: any) => ({
          id: String(supplier.id),
          name: supplier.name,
          invoiceFrequency: supplier.invoice_frequency || '',
          invoiceCurrency: supplier.invoice_currency || '',
          vatStatus: supplier.vat_status || '',
          notes: supplier.notes || '',
          hasInvoicePortalPassword: Boolean(supplier.has_invoice_portal_password),
          invoicePortalUsername: String(supplier.invoice_portal_username || ''),
        }))
      );
    } catch (err) {
      setSupplierError('Failed to load suppliers.');
    }
  };

  const fetchSupplierAliases = async () => {
    try {
      const response = await api.get('/api/suppliers/description-aliases');
      const list = response.data?.aliases || [];
      const byId: Record<string, SupplierAliasApi[]> = {};
      for (const row of list as SupplierAliasApi[]) {
        const sid = String(row.supplier_id);
        if (!byId[sid]) byId[sid] = [];
        byId[sid].push(row);
      }
      setAliasesBySupplier(byId);
    } catch {
      /* tab still usable without alias chips */
    }
  };

  const handleAddCategory = async () => {
    if (!categoryName.trim()) return;
    setCategoryError('');
    try {
      const response = await api.post('/api/categories', {
        name: categoryName.trim(),
        description: categoryDescription.trim(),
        vat_rate: categoryVatRate,
        pl_class: categoryPlClass,
      });
      const category = response.data.category;
      setCategories((prev) => [
        {
          id: String(category.id),
          name: category.name,
          description: category.description || '',
          vatRate: category.vat_rate || '',
          plClass: String(category.pl_class || 'unset'),
        },
        ...prev
      ]);
      setCategoryName('');
      setCategoryDescription('');
      setCategoryPlClass('unset');
      setIsCategoryModalOpen(false);
    } catch (err: any) {
      setCategoryError(err.response?.data?.error || 'Failed to add category.');
    }
  };

  const handleAddSupplier = async () => {
    if (!supplierName.trim()) return;
    setSupplierError('');
    try {
      const response = await api.post('/api/suppliers', {
        name: supplierName.trim(),
        invoice_frequency: supplierFrequency,
        invoice_currency: supplierCurrency,
        vat_status: supplierVatStatus,
        notes: supplierNotes.trim()
      });
      const supplier = response.data.supplier;
      setSuppliers((prev) => [
        {
          id: String(supplier.id),
          name: supplier.name,
          invoiceFrequency: supplier.invoice_frequency || '',
          invoiceCurrency: supplier.invoice_currency || '',
          vatStatus: supplier.vat_status || '',
          notes: supplier.notes || ''
        },
        ...prev
      ]);
      setSupplierName('');
      setSupplierNotes('');
      setIsSupplierModalOpen(false);
      await fetchSupplierAliases();
    } catch (err: any) {
      setSupplierError(err.response?.data?.error || 'Failed to add supplier.');
    }
  };

  const parseBulkLines = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

  const handleBulkAddCategories = async () => {
    setCategoryBulkError('');
    setCategoryBulkSuccess('');
    const lines = parseBulkLines(categoryBulkText);
    if (lines.length === 0) {
      setCategoryBulkError('Paste at least one category line.');
      return;
    }

    const results = await Promise.allSettled(
      lines.map((line) => {
        const [nameRaw, descriptionRaw, vatRateRaw] = line.split(',').map((part) => part.trim());
        const name = nameRaw || '';
        if (!name) {
          return Promise.reject(new Error('Category name is required.'));
        }
        const payload: Record<string, string> = { name };
        if (descriptionRaw) payload.description = descriptionRaw;
        if (vatRateRaw) payload.vat_rate = vatRateRaw;
        return api.post('/api/categories', payload);
      })
    );

    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      setCategoryBulkError(`Failed to add ${failed.length} categories. Please check the format.`);
    } else {
      setCategoryBulkSuccess(`Added ${results.length} categories successfully.`);
      setCategoryBulkText('');
    }
    const createdIds = results
      .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
      .map((result) => String(result.value.data?.category?.id))
      .filter((id) => id);
    setSelectedCategoryIds(createdIds);
    await fetchCategories();
  };

  const handleBulkAddSuppliers = async () => {
    setSupplierBulkError('');
    setSupplierBulkSuccess('');
    const lines = parseBulkLines(supplierBulkText);
    if (lines.length === 0) {
      setSupplierBulkError('Paste at least one supplier line.');
      return;
    }

    const results = await Promise.allSettled(
      lines.map((line) => {
        const parts = line.split(',').map((part) => part.trim());
        const name = parts[0] || '';
        if (!name) {
          return Promise.reject(new Error('Supplier name is required.'));
        }
        const payload: Record<string, string> = { name };
        if (parts[1]) payload.invoice_frequency = parts[1];
        if (parts[2]) payload.invoice_currency = parts[2];
        if (parts[3]) payload.vat_status = parts[3];
        const notes = parts.slice(4).join(', ');
        if (notes) payload.notes = notes;
        return api.post('/api/suppliers', payload);
      })
    );

    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      setSupplierBulkError(`Failed to add ${failed.length} suppliers. Please check the format.`);
    } else {
      setSupplierBulkSuccess(`Added ${results.length} suppliers successfully.`);
      setSupplierBulkText('');
    }
    const createdIds = results
      .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
      .map((result) => String(result.value.data?.supplier?.id))
      .filter((id) => id);
    setSelectedSupplierIds(createdIds);
    await fetchSuppliers();
    await fetchSupplierAliases();
  };

  const handleBulkUpdateCategories = async () => {
    if (selectedCategoryIds.length === 0) return;
    setCategoryBulkError('');
    setCategoryBulkSuccess('');
    const updates: Record<string, string | boolean> = {};
    if (categoryBulkVatRate) updates.vat_rate = categoryBulkVatRate;
    if (categoryBulkDescription) updates.description = categoryBulkDescription;
    if (categoryBulkPlClass) updates.pl_class = categoryBulkPlClass;
    if (Object.keys(updates).length === 0) {
      setCategoryBulkError('Set at least one field to update.');
      return;
    }
    const results = await Promise.allSettled(
      selectedCategoryIds.map((id) => api.put(`/api/categories/${id}`, updates))
    );
    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      setCategoryBulkError(`Failed to update ${failed.length} categories.`);
    } else {
      setCategoryBulkSuccess(`Updated ${results.length} categories successfully.`);
      setCategoryBulkVatRate('');
      setCategoryBulkDescription('');
      setCategoryBulkPlClass('');
    }
    await fetchCategories();
  };

  const handleBulkUpdateSuppliers = async () => {
    if (selectedSupplierIds.length === 0) return;
    setSupplierBulkError('');
    setSupplierBulkSuccess('');
    const updates: Record<string, string | boolean> = {};
    if (supplierBulkFrequency) updates.invoice_frequency = supplierBulkFrequency;
    if (supplierBulkCurrency) updates.invoice_currency = supplierBulkCurrency;
    if (supplierBulkVatStatus) updates.vat_status = supplierBulkVatStatus;
    if (supplierBulkNotes) updates.notes = supplierBulkNotes;
    if (Object.keys(updates).length === 0) {
      setSupplierBulkError('Set at least one field to update.');
      return;
    }
    const results = await Promise.allSettled(
      selectedSupplierIds.map((id) => api.put(`/api/suppliers/${id}`, updates))
    );
    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      setSupplierBulkError(`Failed to update ${failed.length} suppliers.`);
    } else {
      setSupplierBulkSuccess(`Updated ${results.length} suppliers successfully.`);
      setSupplierBulkFrequency('');
      setSupplierBulkCurrency('');
      setSupplierBulkVatStatus('');
      setSupplierBulkNotes('');
    }
    await fetchSuppliers();
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TaxIcon color="primary" />
          <Typography variant="h4" component="h1">
            Tax Returns
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="VAT Returns" />
                <Tab label="Annual Returns" />
                <Tab label="Categories" />
                <Tab label="Suppliers" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  VAT Returns
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Track VAT return periods, submission status, and totals on the VAT workspace.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/vat')}>
                  Open VAT
                </Button>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Annual Returns
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review calendar-year cashflow and prepare filings on the Annual Returns workspace
                  (same layout as VAT, full year instead of a two-month period).
                </Typography>
                <Button variant="contained" onClick={() => navigate('/annual-returns')}>
                  Open Annual Returns
                </Button>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add categories used to tag transactions for VAT and annual return calculations.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={() => setIsCategoryBulkOpen(true)}>
                      Bulk add
                    </Button>
                    <Button variant="contained" onClick={() => setIsCategoryModalOpen(true)}>
                      Add category
                    </Button>
                  </Stack>
                </Box>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Bulk update selected categories
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>VAT rate</InputLabel>
                        <Select
                          value={categoryBulkVatRate}
                          label="VAT rate"
                          onChange={(e) => setCategoryBulkVatRate(String(e.target.value))}
                        >
                          {VAT_RATES.map((rate) => (
                            <MenuItem key={rate} value={rate}>
                              {rate}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Description"
                        value={categoryBulkDescription}
                        onChange={(e) => setCategoryBulkDescription(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>P&L class</InputLabel>
                        <Select
                          value={categoryBulkPlClass}
                          label="P&L class"
                          onChange={(e) => setCategoryBulkPlClass(String(e.target.value))}
                        >
                          <MenuItem value="">Leave unchanged</MenuItem>
                          {PL_CLASS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="contained" onClick={handleBulkUpdateCategories}>
                        Apply to selected ({selectedCategoryIds.length})
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                {categoryError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {categoryError}
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={[
                        { key: 'select', pct: 5 },
                        { key: 'category', pct: 22 },
                        { key: 'description' },
                        { key: 'pl_class', pct: 16 },
                        { key: 'vat_rate', pct: 12 },
                      ]}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={categories.length > 0 && selectedCategoryIds.length === categories.length}
                            indeterminate={
                              selectedCategoryIds.length > 0 && selectedCategoryIds.length < categories.length
                            }
                            onChange={(e) =>
                              setSelectedCategoryIds(
                                e.target.checked ? categories.map((category) => category.id) : []
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>P&L class</TableCell>
                        <TableCell>VAT rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                            No categories added yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        categories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedCategoryIds.includes(category.id)}
                                onChange={(e) =>
                                  setSelectedCategoryIds((prev) =>
                                    e.target.checked
                                      ? [...prev, category.id]
                                      : prev.filter((id) => id !== category.id)
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx} title={category.name}>{category.name}</Box>
                            </TableCell>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx} title={category.description || ''}>{category.description || '—'}</Box>
                            </TableCell>
                            <TableCell>{plClassLabel(category.plClass)}</TableCell>
                            <TableCell>{category.vatRate || '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Suppliers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Canonical supplier names live here. Use <strong>Statement mappings</strong> to attach bank wording
                      (merchant, description, reference) without creating extra supplier rows.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={() => setIsSupplierBulkOpen(true)}>
                      Bulk add
                    </Button>
                    <Button variant="contained" onClick={() => setIsSupplierModalOpen(true)}>
                      Add supplier
                    </Button>
                  </Stack>
                </Box>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Bulk update selected suppliers
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Invoice frequency</InputLabel>
                        <Select
                          value={supplierBulkFrequency}
                          label="Invoice frequency"
                          onChange={(e) => setSupplierBulkFrequency(String(e.target.value))}
                        >
                          {INVOICE_FREQUENCIES.map((freq) => (
                            <MenuItem key={freq} value={freq}>
                              {freq}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Invoice currency</InputLabel>
                        <Select
                          value={supplierBulkCurrency}
                          label="Invoice currency"
                          onChange={(e) => setSupplierBulkCurrency(String(e.target.value))}
                        >
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>VAT status</InputLabel>
                        <Select
                          value={supplierBulkVatStatus}
                          label="VAT status"
                          onChange={(e) => setSupplierBulkVatStatus(String(e.target.value))}
                        >
                          {VAT_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Notes"
                        value={supplierBulkNotes}
                        onChange={(e) => setSupplierBulkNotes(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <Button variant="contained" onClick={handleBulkUpdateSuppliers}>
                        Apply to selected ({selectedSupplierIds.length})
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                {supplierError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {supplierError}
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={[
                        { key: 'select', pct: 4 },
                        { key: 'supplier', pct: 14 },
                        { key: 'statement_mappings', pct: 22 },
                        { key: 'portal_login', pct: 12 },
                        { key: 'invoice_frequency', pct: 10 },
                        { key: 'invoice_currency', pct: 8 },
                        { key: 'vat_status', pct: 10 },
                        { key: 'notes' },
                      ]}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={suppliers.length > 0 && selectedSupplierIds.length === suppliers.length}
                            indeterminate={
                              selectedSupplierIds.length > 0 && selectedSupplierIds.length < suppliers.length
                            }
                            onChange={(e) =>
                              setSelectedSupplierIds(
                                e.target.checked ? suppliers.map((supplier) => supplier.id) : []
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Statement mappings</TableCell>
                        <TableCell>Portal login</TableCell>
                        <TableCell>Invoice frequency</TableCell>
                        <TableCell>Invoice currency</TableCell>
                        <TableCell>VAT status</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suppliers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ color: 'text.secondary' }}>
                            No suppliers added yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        suppliers.map((supplier) => (
                          <TableRow key={supplier.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedSupplierIds.includes(supplier.id)}
                                onChange={(e) =>
                                  setSelectedSupplierIds((prev) =>
                                    e.target.checked
                                      ? [...prev, supplier.id]
                                      : prev.filter((id) => id !== supplier.id)
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx} title={supplier.name}>{supplier.name}</Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Stack spacing={0.5}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                                  {(aliasesBySupplier[supplier.id] || []).slice(0, 4).map((a) => (
                                    <Tooltip key={a.id} title={formatSupplierAliasLabel(a)}>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        sx={fitChipSx}
                                        label={truncateMiddle(formatSupplierAliasLabel(a), 34)}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                                {(aliasesBySupplier[supplier.id] || []).length > 4 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{(aliasesBySupplier[supplier.id] || []).length - 4} more
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setSupplierAliasMappingsSupplier(supplier)}
                                >
                                  Manage mappings
                                </Button>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="caption" color="text.secondary">
                                  {supplier.invoicePortalUsername
                                    ? supplier.invoicePortalUsername
                                    : 'No username'}
                                  {supplier.hasInvoicePortalPassword ? ' · password set' : ''}
                                </Typography>
                                {canManageSupplierAliases ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setPortalCredentialsSupplier(supplier)}
                                  >
                                    Portal login
                                  </Button>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell>{supplier.invoiceFrequency}</TableCell>
                            <TableCell>{supplier.invoiceCurrency}</TableCell>
                            <TableCell>{supplier.vatStatus}</TableCell>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx} title={supplier.notes || ''}>{supplier.notes || '—'}</Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <SupplierPortalCredentialsDialog
        supplier={portalCredentialsSupplier}
        open={Boolean(portalCredentialsSupplier)}
        onClose={() => setPortalCredentialsSupplier(null)}
        onSaved={() => void fetchSuppliers()}
      />

      <Dialog open={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              size="small"
            />
            <TextField
              label="Description"
              value={categoryDescription}
              onChange={(e) => setCategoryDescription(e.target.value)}
              size="small"
            />
            <FormControl size="small">
              <InputLabel>P&L class</InputLabel>
              <Select
                value={categoryPlClass}
                label="P&L class"
                onChange={(e) => setCategoryPlClass(String(e.target.value))}
              >
                {PL_CLASS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>VAT rate</InputLabel>
              <Select
                value={categoryVatRate}
                label="VAT rate"
                onChange={(e) => setCategoryVatRate(String(e.target.value))}
              >
                {VAT_RATES.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {rate}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {categoryError && <Alert severity="error">{categoryError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCategoryModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCategory}>
            Add category
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCategoryBulkOpen} onClose={() => setIsCategoryBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk add categories</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              One category per line: <strong>Name</strong>, <strong>Description</strong>, <strong>VAT rate</strong>.
              Leave VAT rate empty to fill later.
            </Typography>
            <TextField
              label="Bulk categories"
              placeholder="Example: Repairs, Maintenance & repairs, 13.5%"
              value={categoryBulkText}
              onChange={(e) => setCategoryBulkText(e.target.value)}
              multiline
              minRows={6}
            />
            {categoryBulkError && <Alert severity="error">{categoryBulkError}</Alert>}
            {categoryBulkSuccess && <Alert severity="success">{categoryBulkSuccess}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCategoryBulkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkAddCategories}>
            Add categories
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isSupplierModalOpen} onClose={() => setIsSupplierModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add supplier</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Supplier name"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              size="small"
            />
            <FormControl size="small">
              <InputLabel>Invoice frequency</InputLabel>
              <Select
                value={supplierFrequency}
                label="Invoice frequency"
                onChange={(e) => setSupplierFrequency(String(e.target.value))}
              >
                {INVOICE_FREQUENCIES.map((freq) => (
                  <MenuItem key={freq} value={freq}>
                    {freq}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Invoice currency</InputLabel>
              <Select
                value={supplierCurrency}
                label="Invoice currency"
                onChange={(e) => setSupplierCurrency(String(e.target.value))}
              >
                {CURRENCIES.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>VAT status</InputLabel>
              <Select
                value={supplierVatStatus}
                label="VAT status"
                onChange={(e) => setSupplierVatStatus(String(e.target.value))}
              >
                {VAT_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={supplierNotes}
              onChange={(e) => setSupplierNotes(e.target.value)}
              size="small"
            />
            {supplierError && <Alert severity="error">{supplierError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSupplierModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSupplier}>
            Add supplier
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isSupplierBulkOpen} onClose={() => setIsSupplierBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk add suppliers</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              One supplier per line: <strong>Name</strong>, <strong>Frequency</strong>, <strong>Currency</strong>,{' '}
              <strong>VAT status</strong>, <strong>Notes</strong>. Leave blanks to fill later.
            </Typography>
            <TextField
              label="Bulk suppliers"
              placeholder="Example: Example Ltd, Monthly, EUR, VAT Registered, Main utilities"
              value={supplierBulkText}
              onChange={(e) => setSupplierBulkText(e.target.value)}
              multiline
              minRows={6}
            />
            {supplierBulkError && <Alert severity="error">{supplierBulkError}</Alert>}
            {supplierBulkSuccess && <Alert severity="success">{supplierBulkSuccess}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSupplierBulkOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkAddSuppliers}>
            Add suppliers
          </Button>
        </DialogActions>
      </Dialog>

      <SupplierAliasMappingsDialog
        open={supplierAliasMappingsSupplier != null}
        supplierId={supplierAliasMappingsSupplier?.id ?? null}
        supplierName={supplierAliasMappingsSupplier?.name ?? ''}
        onClose={() => setSupplierAliasMappingsSupplier(null)}
        canManageSupplierAliases={canManageSupplierAliases}
        onAliasesChanged={fetchSupplierAliases}
      />
    </Container>
  );
};
